import { useCallback, useState } from 'react'
import { useClient } from '../providers/supabase'

interface PendingFactor {
  id: string
  qrCode: string
  secret: string
}

/**
 * Encapsulates the TOTP enrollment + verify flow used by both onboarding
 * (`enableTwoFactor.tsx`) and settings (`twoFactorSettings.tsx`).
 *
 * `startEnrollment()` removes any abandoned unverified factor, then enrolls
 * a new TOTP factor and returns the QR code + secret to display.
 * `verify(code)` issues a challenge and verifies the user-entered code,
 * resolving on success.
 * `cancel()` removes the in-progress factor so the user isn't prompted to
 * step up to aal2 on their next sign-in if they bail out mid-flow.
 */
export function useTotpEnrollment() {
  const client = useClient()

  const [pending, setPending] = useState<PendingFactor | null>(null)
  const [enrolling, setEnrolling] = useState(false)
  const [enrollError, setEnrollError] = useState<Error | null>(null)
  const [verifying, setVerifying] = useState(false)
  const [verifyError, setVerifyError] = useState<Error | null>(null)

  const startEnrollment = useCallback(async () => {
    setEnrollError(null)
    setEnrolling(true)
    try {
      const { data: existing } = await client.auth.mfa.listFactors()
      const stale = (existing?.totp || []).find((f) => f.status === 'unverified')
      if (stale) {
        await client.auth.mfa.unenroll({ factorId: stale.id })
      }

      const { data, error } = await client.auth.mfa.enroll({
        factorType: 'totp',
        // ISO date keeps factor names readable in any locale.
        friendlyName: `HealthShare (${new Date().toISOString().slice(0, 10)})`,
        issuer: 'HealthShare Reimbursement Tracker',
      })
      if (error) { throw error }
      if (!data?.totp) {
        throw new Error('Unable to start TOTP enrollment.')
      }

      setPending({
        id: data.id,
        qrCode: data.totp.qr_code,
        secret: data.totp.secret,
      })
    } catch (err) {
      setEnrollError(err as Error)
    } finally {
      setEnrolling(false)
    }
  }, [client])

  const verify = useCallback(async (code: string) => {
    if (!pending) { throw new Error('Start enrollment first') }
    if (!/^\d{6}$/.test(code.trim())) {
      throw new Error('Enter the 6-digit code from your authenticator app.')
    }

    setVerifyError(null)
    setVerifying(true)
    try {
      const { data: challenge, error: challengeError } =
        await client.auth.mfa.challenge({ factorId: pending.id })
      if (challengeError) { throw challengeError }

      const { error } = await client.auth.mfa.verify({
        factorId: pending.id,
        challengeId: challenge.id,
        code: code.trim(),
      })
      if (error) {
        throw new Error('That code did not match. Please try again.', { cause: error })
      }
      setPending(null)
    } catch (err) {
      setVerifyError(err as Error)
      throw err
    } finally {
      setVerifying(false)
    }
  }, [client, pending])

  const cancel = useCallback(async () => {
    if (pending) {
      try { await client.auth.mfa.unenroll({ factorId: pending.id }) } catch { /* best effort */ }
      setPending(null)
    }
    setVerifyError(null)
  }, [client, pending])

  return {
    pending,
    enrolling,
    enrollError,
    verifying,
    verifyError,
    startEnrollment,
    verify,
    cancel,
  }
}
